import { ChangeEvent, FC } from 'react'
import { NameDto } from '@/api/Common/Types'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { CategoryType } from 'api/Documents/Types/ClientDocument'
import {
  FormBaseButtonContainer,
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
} from 'components/Form/Form.styles'
import { Field, FormikErrors, FormikProps, withFormik } from 'formik'

import styles from './ClientDetailsDocument.module.scss'

export interface BulkUpdateProps {
  clientDocumentTypes: NameDto[]
  clientDocumentCategoryTypes: CategoryType[]
  getDocumentTypeBasedOnCategory(categoryId: string): void

  onCloseModal(): void

  onSubmitForm(values: BulkUpdateFormValues): void
}
export interface BulkUpdateFormValues {
  documentCategory: number | undefined
  documentType: number | undefined
}

const BulkCategoryUpdateFormUI: FC<BulkUpdateProps & FormikProps<BulkUpdateFormValues>> = (
  props
) => {
  const {
    values,
    errors,
    handleSubmit,
    setFieldValue,
    getDocumentTypeBasedOnCategory,
    isSubmitting,
    touched,
    setFieldTouched,
  } = props

  return (
    <div className={styles.container}>
      <FormBaseFieldBlock>
        <FormBaseRow>
          <FormBaseFieldLabel>Category</FormBaseFieldLabel>
          <FormBaseFieldGroup>
            <Field
              component='select'
              name='documentCategory'
              onChange={async (event: ChangeEvent<HTMLSelectElement>) => {
                const { value } = event.target
                await setFieldValue('documentCategory', parseInt(value, 10))
                setFieldValue('documentType', undefined)
                getDocumentTypeBasedOnCategory(value)
              }}
            >
              {props.clientDocumentCategoryTypes.map((docCategory) => (
                <option key={docCategory.id} value={docCategory.id}>
                  {docCategory.name}
                </option>
              ))}
            </Field>
          </FormBaseFieldGroup>
        </FormBaseRow>
        {errors.documentCategory && (
          <FormBaseErrorMessage>{errors.documentCategory}</FormBaseErrorMessage>
        )}
      </FormBaseFieldBlock>
      <FormBaseFieldBlock>
        <FormBaseRow>
          <FormBaseFieldLabel>Type</FormBaseFieldLabel>
          <FormBaseFieldGroup>
            <Field
              component='select'
              name='documentType'
              onChange={async (event: ChangeEvent<HTMLSelectElement>) => {
                await setFieldValue('documentType', parseInt(event.target.value, 10))
                setFieldTouched('documentType')
              }}
            >
              <option value={''}>None</option>
              {props.clientDocumentTypes.map((docType) => (
                <option key={docType.id} value={docType.id}>
                  {docType.name}
                </option>
              ))}
            </Field>
          </FormBaseFieldGroup>
        </FormBaseRow>
        {touched.documentType && errors.documentType && (
          <FormBaseErrorMessage>{errors.documentType}</FormBaseErrorMessage>
        )}
      </FormBaseFieldBlock>
      <FormBaseButtonContainer>
        <ButtonGroup>
          <Button type='button' variant='outline' onClick={props.onCloseModal}>
            Cancel
          </Button>
          <Button
            type='button'
            onClick={() => handleSubmit()}
            disabled={isSubmitting || !values.documentCategory || !values.documentType}
          >
            Save
          </Button>
        </ButtonGroup>
      </FormBaseButtonContainer>
    </div>
  )
}

export const BulkCategoryUpdateForm = withFormik<BulkUpdateProps, BulkUpdateFormValues>({
  mapPropsToValues: () => ({ documentCategory: undefined, documentType: undefined }),
  handleSubmit: async (values, { setSubmitting, props }) => {
    setSubmitting(true)
    await props.onSubmitForm(values)
    setSubmitting(false)
  },
  validate: (values) => {
    const errors: FormikErrors<BulkUpdateFormValues> = {}
    if (!values.documentCategory) {
      errors.documentCategory = 'Required'
    }
    if (!values.documentType) {
      errors.documentType = 'Required'
    }
    return errors
  },
})(BulkCategoryUpdateFormUI)
